// This file is auto-generated by @hey-api/openapi-ts

import * as z from 'zod';

/**
 * ClaimResult
 *
 * Evidence-based claim result from extracted_claims (Consensus-style).
 */
export const zClaimResult = z.object({
    claim_id: z.uuid(),
    claim_text: z.string(),
    claim_type: z.string(),
    cosine_sim: z.number().optional().default(0),
    excerpt: z.string().nullish(),
    final_score: z.number(),
    page_number: z.int().nullish(),
    parent_authors: z.array(z.string()).nullish(),
    parent_document_id: z.uuid(),
    parent_journal: z.string().nullish(),
    parent_title: z.string(),
    parent_year: z.int().nullish(),
    reasons: z.array(z.record(z.string(), z.unknown())).optional(),
    workspace_id: z.uuid()
});

/**
 * ComponentCheck
 */
export const zComponentCheck = z.object({
    latency_ms: z.int().nullish(),
    msg: z.string().nullish(),
    ok: z.boolean()
});

/**
 * FacetCounts
 *
 * Aggregated facet counts for UI filter panel.
 */
export const zFacetCounts = z.object({
    category: z.record(z.string(), z.int()).optional(),
    geography_tags: z.record(z.string(), z.int()).optional(),
    journal: z.record(z.string(), z.int()).optional(),
    methodology_tags: z.record(z.string(), z.int()).optional(),
    status: z.record(z.string(), z.int()).optional(),
    type: z.record(z.string(), z.int()).optional(),
    year: z.record(z.string(), z.int()).optional()
});

/**
 * FeedbackRequest
 */
export const zFeedbackRequest = z.object({
    action: z.string(),
    document_id: z.uuid(),
    query_id: z.uuid().nullish(),
    search_query: z.string().nullish(),
    source_surface: z.string().optional().default('search'),
    workspace_id: z.uuid()
});

/**
 * HealthDetailResponse
 */
export const zHealthDetailResponse = z.object({
    all_circuits_closed: z.boolean(),
    circuit_breakers: z.record(z.string(), z.unknown()),
    db: z.record(z.string(), z.unknown()),
    environment: z.string(),
    meili: zComponentCheck,
    redis: z.record(z.string(), z.unknown()),
    service: z.string(),
    took_ms: z.int(),
    version: z.string()
});

/**
 * ReindexRequest
 */
export const zReindexRequest = z.object({
    entity_type: z.string().optional().default('paper'),
    reset_dead_letters: z.boolean().optional().default(true),
    workspace_id: z.string().nullish()
});

/**
 * ReindexResponse
 */
export const zReindexResponse = z.object({
    message: z.string(),
    requeued: z.int(),
    status: z.string()
});

/**
 * SearchFilters
 *
 * Facet/filter constraints applied to search results.
 */
export const zSearchFilters = z.object({
    category: z.string().nullish(),
    collection_type: z.string().nullish(),
    entity_types: z.array(z.string()).optional(),
    geography_tags: z.array(z.string()).optional(),
    has_figures: z.boolean().nullish(),
    has_tables: z.boolean().nullish(),
    is_key_source: z.boolean().nullish(),
    journal: z.array(z.string()).optional(),
    journal_tier: z.string().nullish(),
    methodology: z.array(z.string()).optional(),
    parent_document_id: z.uuid().nullish(),
    status: z.string().nullish(),
    type: z.array(z.string()).optional(),
    year_max: z.int().nullish(),
    year_min: z.int().nullish()
});

/**
 * SearchResult
 *
 * Single search result with scoring signals and explainability.
 */
export const zSearchResult = z.object({
    authors: z.array(z.string()).nullish(),
    badges: z.array(z.string()).optional(),
    bm25_rank: z.int().nullish(),
    card_state: z.string().optional().default('default'),
    chunk_section: z.string().nullish(),
    chunk_snippet: z.string().nullish(),
    citation_count: z.int().optional().default(0),
    collection_id: z.uuid().nullish(),
    collection_ids: z.array(z.string()).optional(),
    collection_type: z.string().nullish(),
    content: z.string().nullish(),
    document_id: z.string().nullish(),
    doi: z.string().nullish(),
    external_id: z.string().nullish(),
    external_url: z.string().nullish(),
    final_score: z.number(),
    geography_tags: z.array(z.string()).nullish(),
    has_figures: z.boolean().optional().default(false),
    has_tables: z.boolean().optional().default(false),
    id: z.uuid(),
    is_key_source: z.boolean().optional().default(false),
    item_count: z.int().nullish(),
    journal: z.string().nullish(),
    linked_document_ids: z.array(z.string()).nullish(),
    methodology_tags: z.array(z.string()).nullish(),
    objective_id: z.string().nullish(),
    parent_document_id: z.uuid().nullish(),
    reason: z.string().nullish(),
    reasons: z.array(z.record(z.string(), z.unknown())).optional(),
    reranker_score: z.number().nullish(),
    rrf_score: z.number().optional().default(0),
    search_eligibility: z.enum([
        'fully_enriched',
        'metadata_only',
        'searchable_partial',
        'pending'
    ]).nullish(),
    session_category: z.string().nullish(),
    session_status: z.string().nullish(),
    source: z.string().optional().default('library'),
    tags: z.array(z.string()).optional(),
    title: z.string(),
    type: z.string().optional().default('paper'),
    updated_at: z.string().nullish(),
    vector_rank: z.int().nullish(),
    visibility: z.string().nullish(),
    word_count: z.int().nullish(),
    workspace_boost: z.number().optional().default(0),
    workspace_id: z.uuid(),
    year: z.int().nullish()
});

/**
 * SearchTab
 *
 * Determines which pipeline and data sources to use.
 *
 * Three core scopes:
 * smart    — hybrid search across all workspace documents (BM25 + pgvector + reranker)
 * library  — same pipeline, optionally scoped to a collection via collection_id
 * discover — external discovery fan-out (S2 + OpenAlex + Exa + Crossref)
 */
export const zSearchTab = z.enum([
    'smart',
    'library',
    'discover'
]);

/**
 * SearchResponse
 *
 * Outgoing search response.
 */
export const zSearchResponse = z.object({
    claim_results: z.array(zClaimResult).optional(),
    classified_intent: z.string(),
    degrade_reasons: z.array(z.string()).optional(),
    degraded: z.boolean().optional().default(false),
    facets: zFacetCounts.optional(),
    has_more: z.boolean().optional().default(false),
    page: z.int(),
    page_size: z.int(),
    query: z.string(),
    query_id: z.uuid().optional(),
    results: z.array(zSearchResult),
    tab: zSearchTab,
    timings_ms: z.record(z.string(), z.int()).optional(),
    took_ms: z.int(),
    total: z.int()
});

/**
 * SortOrder
 */
export const zSortOrder = z.enum([
    'relevance',
    'date_desc',
    'date_asc',
    'citation_desc'
]);

/**
 * SearchRequest
 *
 * Incoming POST /v1/search body.
 */
export const zSearchRequest = z.object({
    collection_id: z.uuid().nullish(),
    document_id: z.uuid().nullish(),
    filters: zSearchFilters.optional(),
    mode: z.string().optional().default('auto'),
    page: z.int().gte(1).optional().default(1),
    page_size: z.int().gte(1).lte(200).optional().default(10),
    query: z.string().min(1).max(500),
    sort: zSortOrder.optional().default('relevance'),
    tab: zSearchTab.optional().default('smart')
});

/**
 * SuggestResponse
 */
export const zSuggestResponse = z.object({
    suggestions: z.array(z.string()),
    took_ms: z.int()
});

/**
 * ValidationError
 */
export const zValidationError = z.object({
    ctx: z.record(z.string(), z.unknown()).optional(),
    input: z.unknown().optional(),
    loc: z.array(z.union([z.string(), z.int()])),
    msg: z.string(),
    type: z.string()
});

/**
 * HTTPValidationError
 */
export const zHttpValidationError = z.object({
    detail: z.array(zValidationError).optional()
});

/**
 * Health details
 */
export const zHealthDetailHealthGetResponse = zHealthDetailResponse;

export const zReindexV1AdminReindexPostBody = zReindexRequest;

/**
 * Reindex result summary
 */
export const zReindexV1AdminReindexPostResponse = zReindexResponse;

export const zSearchV1SearchPostBody = zSearchRequest;

/**
 * Ranked search results
 */
export const zSearchV1SearchPostResponse = zSearchResponse;

export const zSubmitFeedbackV1SearchFeedbackPostBody = zFeedbackRequest;

/**
 * No content
 */
export const zSubmitFeedbackV1SearchFeedbackPostResponse = z.void();

export const zSearchStreamV1SearchStreamPostBody = zSearchRequest;

export const zSuggestV1SearchSuggestGetQuery = z.object({
    q: z.string().min(1).max(100),
    limit: z.int().gte(1).lte(10).optional().default(5)
});

/**
 * Suggestion list
 */
export const zSuggestV1SearchSuggestGetResponse = zSuggestResponse;
